import React, { useEffect, useRef, useState } from "react";
import { Bell, Target, Swords, Activity, Trash2, Trash, Stethoscope } from "lucide-react";
import api from "@/api";

const TABS = [
  { key: "all", label: "الكل", types: null },
  { key: "target", label: "90%", types: ["target_90"] },
  { key: "challenge", label: "التحديات", types: ["challenge", "challenge_done"] },
  { key: "coordinator", label: "نشاط المنسقين", types: ["opportunity", "activity"] },
  { key: "plans", label: "الخطط المتأخرة", types: ["plan_overdue"] },
];

const iconFor = (t) => {
  if (t === "target_90") return Target;
  if (t === "plan_overdue") return Stethoscope;
  if (t === "challenge" || t === "challenge_done") return Swords;
  return Activity;
};

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const ref = useRef();

  const load = () => {
    api.get("/notifications").then((r) => { setItems(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/read-all"); setUnread(0); load(); } catch { /* ignore */ }
    }
  };

  const removeOne = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/notifications/${id}`); setItems((prev) => prev.filter((n) => n.id !== id)); } catch { /* ignore */ }
  };

  const clearAll = async () => {
    try { await api.delete("/notifications"); setItems([]); setUnread(0); } catch { /* ignore */ }
  };

  const fmt = (d) => { try { return new Date(d).toLocaleString("ar"); } catch { return ""; } };

  const activeTypes = TABS.find((t) => t.key === tab)?.types;
  const filtered = activeTypes ? items.filter((n) => activeTypes.includes(n.type)) : items;

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notifications-bell" onClick={toggle} className="relative p-2 rounded-lg hover:bg-sky-100 text-sky-600 transition">
        <Bell size={20} />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-0.5 -left-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="notif-panel" className="fixed sm:absolute inset-x-2 sm:inset-x-auto top-16 sm:top-auto sm:left-0 mt-2 sm:w-96 glass rounded-2xl shadow-xl border border-sky-200/50 z-[70] p-2" dir="rtl">
          <div className="flex items-center justify-between px-2 py-2 border-b border-sky-200/40">
            <span className="font-head font-bold text-sm text-stone-800">الإشعارات</span>
            {items.length > 0 && (
              <button data-testid="notif-clear-all" onClick={clearAll} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"><Trash size={14} /> مسح الكل</button>
            )}
          </div>

          <div className="flex gap-1 p-2 overflow-x-auto">
            {TABS.map((t) => {
              const count = t.types ? items.filter((n) => t.types.includes(n.type)).length : items.length;
              return (
                <button
                  key={t.key}
                  data-testid={`notif-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition ${tab === t.key ? "bg-sky-500 text-white" : "bg-sky-100/60 text-sky-700 hover:bg-sky-100"}`}
                >
                  {t.label} {count > 0 && <span className="opacity-80">({count})</span>}
                </button>
              );
            })}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {!filtered.length ? (
              <p className="text-center text-stone-500 text-sm py-8">لا توجد إشعارات</p>
            ) : filtered.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <div key={n.id} data-testid={`notif-item-${n.id}`} className={`group flex gap-2 p-3 rounded-xl my-1 ${n.read ? "" : "bg-sky-50/70"}`}>
                  <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0"><Icon size={16} className="text-emerald-600" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-stone-800">{n.title}</div>
                    <div className="text-xs text-stone-600">{n.message}</div>
                    <div className="text-[10px] text-stone-400 mt-1">{fmt(n.created_at)}</div>
                  </div>
                  <button data-testid={`notif-del-${n.id}`} onClick={(e) => removeOne(e, n.id)} className="p-1 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition self-start" title="حذف">
                    <Trash2 size={15} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
